import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import BusinessCard from "@/components/BusinessCard";
import { Button } from "@/components/ui/button";
import { MapPin, Loader2, Building2, ArrowRight } from "lucide-react";

function setMeta(name, content, attr = "name") {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

export default function MetroPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const state = params.get("state") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!state) { setErr(true); setLoading(false); return; }
    setLoading(true); setErr(false);
    api.get("/metro-page", { params: { slug, state } })
      .then(({ data }) => setData(data))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  }, [slug, state]);

  useEffect(() => {
    if (!data) return;
    const m = data.metro;
    const title = `Local businesses across the ${m.name} area, ${m.state_abbr} | Saving America Money`;
    const desc = `Discover ${data.businesses.length} local businesses across ${m.city_count} cities in the ${m.name}, ${m.state_abbr} metro on Saving America Money.`;
    document.title = title;
    setMeta("description", desc);
    setMeta("og:title", title, "property");
    setMeta("og:description", desc, "property");
    const ogImg = `${process.env.REACT_APP_BACKEND_URL}/api/og-image?kind=metro&title=${encodeURIComponent(m.name + " area")}&subtitle=${encodeURIComponent(`${data.businesses.length} businesses · ${m.city_count} cities · ${m.state_abbr}`)}`;
    setMeta("og:image", ogImg, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:image", ogImg);
    let canon = document.head.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
    canon.setAttribute("href", `${window.location.origin}/metro/${m.slug}?state=${m.state_abbr}`);
    return () => {
      document.title = "Saving America Money";
      ['link[rel="canonical"]', 'meta[property="og:title"]', 'meta[property="og:description"]',
       'meta[property="og:image"]', 'meta[name="twitter:card"]', 'meta[name="twitter:image"]'].forEach((s) => document.head.querySelector(s)?.remove());
    };
  }, [data]);

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-crimson" /></div>;
  if (err || !data) return (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-2xl font-bold text-sam-navy">Metro not found</h1>
      <Button asChild className="mt-6 bg-sam-navy hover:bg-sam-navy2"><Link to="/browse">Browse all businesses</Link></Button>
    </div>
  );

  const { metro, businesses, cities } = data;
  const withListings = cities.filter((c) => c.count > 0);

  return (
    <div data-testid="metro-page" className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
      <div className="rounded-2xl bg-gradient-to-br from-sam-navy to-sam-navy2 text-sam-cream p-8 sm:p-12 mb-8">
        <div className="flex items-center gap-2 text-sam-cream/70 text-sm font-semibold uppercase tracking-wider"><MapPin className="h-4 w-4" />{metro.state_abbr} metro</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mt-2" data-testid="metro-page-title">The {metro.name} area</h1>
        <p className="text-sam-cream/80 mt-3 max-w-2xl text-base">
          {businesses.length} local {businesses.length === 1 ? "business" : "businesses"} across {metro.city_count} {metro.city_count === 1 ? "city" : "cities"} in the {metro.name} market.
        </p>
        <Button asChild className="mt-6 bg-sam-crimson hover:bg-sam-crimson/90 rounded-full">
          <Link to={`/browse?state=${metro.state_abbr}`}>Browse {metro.state_abbr} <ArrowRight className="ml-1 h-4 w-4" /></Link>
        </Button>
      </div>

      {withListings.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="metro-page-cities">
          {withListings.map((c) => (
            <Link key={c.slug} to={`/city/${c.slug}?state=${c.state_abbr}`} data-testid={`metro-city-link-${c.slug}`}
              className="text-sm px-3 py-1.5 rounded-full border border-sam-crimson/40 text-sam-navy hover:bg-sam-crimson hover:text-white transition">
              {c.name} · {c.count}
            </Link>
          ))}
        </div>
      )}

      {businesses.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="metro-page-results">
          {businesses.map((b, i) => <BusinessCard key={b.id} b={b} index={i} />)}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border p-10 text-center" data-testid="metro-page-empty">
          <Building2 className="h-8 w-8 text-sam-slate mx-auto mb-3" />
          <p className="font-display text-lg text-sam-navy">No listings in the {metro.name} area yet.</p>
        </div>
      )}
    </div>
  );
}
